<template>
  <!-- Comments START -->
  <div v-if="item.id && !item.isTemplate">
    <hr />
    <div class="label mt-2 mb-1">
      <div class="d-flex justify-content-between">
        <div>Comments</div>

        <div class="font-weight-bold cursor-pointer text-2xl" v-b-tooltip.hover :title="copyEmailToBoardTitle" @click="copyEmailToBoard">
          <i class="far fa-at fa-lg"></i>
        </div>
      </div>
    </div>

    <div class="position-relative">
      <textarea-autosize
        class="form-control comment-textarea"
        style="padding-right: 30px"
        rows="3"
        placeholder="Add a comment or paste an image..."
        v-model="comment"
        :min-height="75"
        @paste.native="paste"
        ref="commentInput"
        @keydown.native="handleEnter"
      ></textarea-autosize>
      <div
        class="form-control comment-textarea whitespace-pre-wrap"
        style="background: #eaeaea; font-size: 0.9rem; height: auto; white-space: pre-wrap"
        v-if="replyComment"
      >
        <div class="d-flex justify-content-between">
          <div class="font-weight-bold">In reply to {{ replyComment.userTlc }}:</div>
          <div @click="replyComment = null" class="font-weight-bold cursor-pointer"><i class="far fa-times"></i></div>
        </div>

        <div class="ml-4 pl-2" style="border-left: solid 2px #eaeaea">
          {{ replyComment.text }}
        </div>
      </div>

      <div
        class="form-control comment-textarea whitespace-pre-wrap"
        style="background: #eaeaea; font-size: 0.9rem; height: auto; white-space: pre-wrap"
        v-if="replyAttachment"
      >
        <div class="d-flex justify-content-between">
          <div class="font-weight-bold">
            Commenting on file
            <i class="far fa-file"></i>
            {{ replyAttachment.fileName }}:
          </div>
          <div @click="replyAttachment = null" class="font-weight-bold cursor-pointer"><i class="far fa-times"></i></div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <button class="btn btn-sm btn-success mt-2" :disabled="!comment" @click="saveComment()">
        <i class="far fa-save"></i>
        Save comment
      </button>
    </div>

    <div v-if="showCommentsSection()">
      <hr />
      <div class="comments">
        <div v-for="comment in comments" class="comment" :key="comment.id">
          <div class="container-fluid">
            <div class="row">
              <div class="avatar col-1">
                <div v-if="comment.isSystemComment">SYS</div>
                <div v-else v-b-tooltip.hover.right :title="comment.userUserName">
                  {{ comment.userTlc }}
                </div>
              </div>

              <div class="ml-1 col-11 mb-1" style="margin-right: -10px">
                <div class="container">
                  <div class="row comment-header flex justify-content-between">
                    <div class="flex">
                      <span class="inline-block">
                        {{ comment.createdDate | formatDate }}
                      </span>
                      <span class="inline-block ml-1" v-if="comment.updatedDate && comment.updatedDate !== comment.createdDate">
                        (Edited: {{ comment.updatedDate | formatDate }})
                      </span>
                    </div>
                    <div>
                      <div v-if="couldEditComment(comment)" class="pl-2 replyToButton" @click="startEditComment(comment)">
                        <i class="far fa-pencil"></i>
                        Edit
                      </div>
                      <div class="pl-2 replyToButton" v-if="!comment.isSystemComment" @click="replyToComment(comment)">
                        <i class="far fa-reply"></i>
                        Reply
                      </div>
                    </div>
                  </div>
                </div>

                <div class="container" v-if="!isEditing(comment.id)">
                  <div class="row">
                    <div class="col-12">
                      <div :ref="'comment' + comment.id" class="comment-text" :class="{ 'comment-system': comment.isSystemComment }">
                        <div class="mb-2" v-if="comment.attachment">
                          <div class="font-weight-light underline cursor-pointer" @click="downloadAttachment(comment.attachment)">
                            <i class="far fa-file"></i>
                            {{ comment.attachment.fileName }}
                          </div>
                        </div>
                        <div v-linkify-directive="comment.text"></div>
                      </div>

                      <div
                        v-if="comment.parentComment"
                        class="comment-reply cursor-pointer"
                        :class="{ 'comment-system': comment.isSystemComment }"
                        @click="scrollToElement(comment.parentComment.id)"
                      >
                        <div class="font-weight-light">In reply to {{ comment.parentComment.userTlc }}:</div>
                        <div class="comment-reply-text" v-linkify-directive="comment.parentComment.text"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div class="position-relative">
                    <textarea-autosize
                      class="form-control comment-textarea"
                      rows="2"
                      placeholder="Add a comment..."
                      v-model="comment.text"
                      :min-height="50"
                      required
                    ></textarea-autosize>
                  </div>
                  <div class="d-flex justify-content-end">
                    <button class="btn btn-sm btn-success mt-2 mr-3" :disabled="!comment.text" @click="updateComment(comment)">
                      <i class="far fa-save"></i>
                      Save comment
                    </button>
                    <button class="btn btn-sm btn-primary mt-2" @click="cancelEditComment(comment)">
                      <i class="far fa-times"></i>
                      Cancel
                    </button>
                  </div>
                </div>

                <div v-if="comment.isChildItem">
                  <small>
                    Comment from linked item -
                    <router-link
                      :to="{
                        name: 'BoardItemDetail',
                        params: { itemid: comment.parentId },
                      }"
                    >
                      {{ comment.parentTitle }}
                    </router-link>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import lodash from 'lodash';
  import { BoardModule, BoardStore } from '@/store/board';
  import { UserModule, UserStore } from '@/store/user';

  import {
    BoardItemCommentDto,
    IBoardItemDto,
    IBoardItemCommentDto,
    AttachmentDestination,
    IAttachmentDto,
  } from '@/apiclient/apiclient_generated';
  import { AttachmentsModule, AttachmentsStore } from '@/store/attachments';
  import FileSaver from 'file-saver';
  import { Prop, Watch } from "vue-property-decorator";

  @Component({
    components: {},
  })
  export default class TaskDetailComments extends Vue {
    @Prop() value: boolean;

    private userStore: UserModule = UserStore;
    private boardStore: BoardModule = BoardStore;
    private attachmentssStore: AttachmentsModule = AttachmentsStore;

    private replyComment: IBoardItemCommentDto = null;
    private replyAttachment: IAttachmentDto = null;
    copyEmailToBoardTitle = 'Click to copy E-Mail to Board identifier';

    private comment = '';
    editdescription = false;
    isSaving = false;

    private activeComments = [];

    get readOnly(): boolean {
      return this.item ? this.item.isArchived || this.item.isReadonly : false;
    }

    mounted() {
      this.$root.$on('commentonattachment', (data) => {
        this.replyAttachment = data;
        ((this.$refs.commentInput as any).$el as any).focus();
      });
    }

    destroyed() {
      if (!this.item.boardItemTypeId && this.item.id) {
        this.boardStore.resetBoardItemType(this.item.id);
      }
      this.$root.$off('commentonattachment');
    }

    paste(evt) {
      const items = (evt.clipboardData || evt.originalEvent.clipboardData).items;
      for (const index in items) {
        const item = items[index];
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          const reader = new FileReader();

          reader.onload = async (e) => {
            await this.attachmentssStore.addAttachmentFromBase64({
              destination: AttachmentDestination.BoardItem,
              id: this.item.id,
              imageData: e.target.result,
            });
          };
          reader.readAsDataURL(blob);
        }
      }
    }

    get item(): IBoardItemDto {
      return this.boardStore.currentBoardItem;
    }

    startEditComment(comment) {
      if (!this.activeComments.includes(comment.id)) {
        comment.backup = comment.text;
        this.activeComments.push(comment.id);
      }
    }

    async updateComment(comment: BoardItemCommentDto) {
      const updatedComment = await this.boardStore.updateComment(comment);
      comment.createdDate = updatedComment.createdDate;
      comment.updatedDate = updatedComment.updatedDate;

      const index = this.activeComments.indexOf(comment.id);
      if (index > -1) {
        this.activeComments.splice(index, 1);
      }
    }

    cancelEditComment(comment) {
      const index = this.activeComments.indexOf(comment.id);
      if (index > -1) {
        this.activeComments.splice(index, 1);
        comment.text = comment.backup;
      }
    }

    replyToComment(comment) {
      this.replyComment = comment;
      ((this.$refs.commentInput as any).$el as any).focus();
    }

    couldEditComment(comment: IBoardItemCommentDto) {
      return !comment.isSystemComment && comment.userId === this.userStore.user.id && !this.item.isArchived;
    }

    isEditing(commentId) {
      return this.activeComments.includes(commentId);
    }

    async saveComment() {
      const comment = {} as BoardItemCommentDto;
      comment.boardItemId = this.item.id;
      comment.text = this.comment;
      comment.parentCommentId = this.replyComment ? this.replyComment.id : null;
      comment.attachmentId = this.replyAttachment ? this.replyAttachment.id : null;

      await this.boardStore.saveComment(comment);
      this.comment = '';
      this.replyComment = null;
      this.replyAttachment = null;
    }

    get comments() {
      const comments = this.item.linkedBoardItems.reduce((prev, curr) => {
        curr.comments.forEach((_: any) => {
          _.parentId = curr.id;
          _.parentTitle = curr.title;
        });

        return [...prev, ...curr.comments];
      }, []);

      if (comments.length > 0) {
        comments.forEach((_) => (_.isChildItem = true));
      }

      return lodash.orderBy([...this.item.comments, ...comments], 'createdDate', 'desc');
    }

    showCommentsSection() {
      return this.item.comments?.length > 0 || this.item.linkedBoardItems?.some((_) => _.comments.length > 0);
    }

    copyEmailToBoard() {
      const marker = '#BI:' + this.item.id + '#';
      navigator.clipboard.writeText(marker).then(
        () => {
          const originalTitle = this.copyEmailToBoardTitle;
          this.copyEmailToBoardTitle = 'Copied!';

          setTimeout(() => {
            this.copyEmailToBoardTitle = originalTitle;
          }, 2000);
        },
        function (err) {
          console.log(err);
        }
      );
    }

    scrollToElement(id) {
      const el = this.$refs['comment' + id];
      if (el) {
        el[0].classList.add('highlight');
        el[0].scrollIntoView({ behavior: 'smooth' });

        setTimeout(() => {
          el[0].classList.remove('highlight');
        }, 2000);
      }
    }

    async downloadAttachment(attachment: IAttachmentDto) {
      const fileData = await this.attachmentssStore.downloadAttachment(attachment.id);
      FileSaver.saveAs(fileData.data, fileData.fileName);
    }

    @Watch('comment')
    commentEntered() {
      this.$emit('input', !!this.comment);
    }

    handleEnter(e: KeyboardEvent) {
      if (e.key.toLowerCase() == 'enter' && e.ctrlKey && this.comment) {
        this.saveComment();
      }
    }
  }
</script>

<style scoped lang="scss">
  .comment-textarea {
    box-shadow: 0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 0 1px rgba(9, 30, 66, 0.08);
  }

  @keyframes highlight {
    0% {
      background-color: yellow;
    }
    100% {
      background-color: white;
    }
  }

  .comments {
    padding: 5px;
    border-radius: 3px;

    .comment {
      padding: 10px;
      display: flex;

      .replyToButton {
        cursor: pointer;
        font-weight: bold;
        visibility: hidden;
        opacity: 0;
        display: inline-block;
        transition: opacity 0.5s;
      }

      &:hover {
        .replyToButton {
          visibility: visible;
          opacity: 1;
        }
      }

      .comment-header {
        font-size: 0.8rem;
        color: #666;
        margin-bottom: 6px;
      }

      .comment-text {
        padding: 8px 12px;
        background: white;
        border-radius: 3px;
        box-shadow: 0 1px 2px -1px rgba(9, 30, 66, 0.25), 0 0 0 1px rgba(9, 30, 66, 0.08);

        &.highlight {
          animation: highlight 2000ms ease-out;
        }

        &.comment-system {
          background: transparent;
          box-shadow: none;
          padding: 8px 0;
          font-size: 0.9rem;
        }
      }

      .comment-reply {
        padding: 8px 12px;

        background: #eaeaea;
        margin-right: 5px;
        margin-left: 5px;
        border: solid 1px #e5e5e5;
        border-top: none;
        border-radius: 0 0 3px 3px;

        .comment-reply-text {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          text-overflow: ellipsis;
          overflow-y: hidden;
        }
      }
    }
  }
</style>
